import ClearIcon from '@mui/icons-material/Clear';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Badge,
  Box,
  CircularProgress,
  Fade,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  getAdvFiltersBtnSx,
  getBadgeIconSx,
  getDefaultIconSx,
  getIconButtonSx,
  getSearchTextfieldSx,
  getTextInputBorderContainerSx,
} from 'components/Search/styles';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { hasLength } from 'utility/functions';
import { useLogSearch } from './LogSearchProvider';
import { LogSearchFilterFields } from './LogSearchFilterFields';
import { LogSearchAdvancedPopover } from './LogSearchAdvancedPopover';

/**
 * Search top bar for the Log Search Modal: a debounced message input, an
 * advanced-filters button (with an applied-count badge) that opens the
 * advanced popover, and the active-filter chips row.
 */
type LogSearchTopBarProps = {
  /** Show the small inline spinner while the log query is in flight. */
  isFetching?: boolean;
};

export const LogSearchTopBar = memo(({ isFetching = false }: LogSearchTopBarProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const {
    filters,
    updateFilterValue,
    advFilterCount,
    textInputRef,
    textBoxSizeRef,
    handleOpenAdvPopover,
  } = useLogSearch();

  const queryValue = filters.query || '';
  const hasSearchText = hasLength(queryValue);

  const handleTextInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      updateFilterValue('query', event.target.value);
    },
    [updateFilterValue],
  );

  const handleTextInputClear = useCallback(() => {
    updateFilterValue('query', '');
    textInputRef.current?.focus();
  }, [updateFilterValue, textInputRef]);

  const searchTextFieldSx = useMemo(() => getSearchTextfieldSx(theme, false), [theme]);
  const advFiltersBtnSx = useMemo(() => {
    return getAdvFiltersBtnSx(theme, advFilterCount > 0);
  }, [theme, advFilterCount]);

  const strLoading = t('blox.general.loading');
  const strClear = t('blox.actions.clear');
  const strAdvFilters = t('blox.navigation.assets.advanced_filters');
  const strSearch = `${t('blox.search.label')}...`;

  return (
    <Box
      data-testid="log-search-topbar"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        p: 1,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Stack
        ref={textBoxSizeRef}
        direction="row"
        sx={{ alignItems: 'center', gap: 0.5, ...getTextInputBorderContainerSx(theme) }}
      >
        <TextField
          data-testid="log-search-text-input"
          inputRef={textInputRef}
          size="small"
          autoComplete="off"
          value={queryValue}
          onChange={handleTextInputChange}
          placeholder={strSearch}
          variant="outlined"
          fullWidth
          data-private
          sx={searchTextFieldSx}
        />

        <Fade
          in={isFetching}
          appear
          unmountOnExit
        >
          <CircularProgress
            size={14}
            thickness={3}
            color="primary"
            title={strLoading}
          />
        </Fade>

        <Fade
          in={hasSearchText}
          appear
          unmountOnExit
        >
          <Tooltip title={strClear}>
            <IconButton
              sx={getIconButtonSx}
              onClick={handleTextInputClear}
              data-testid="log-search-clear-input"
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Fade>

        <Tooltip title={strAdvFilters}>
          <IconButton
            data-testid="log-search-advanced-button"
            onClick={handleOpenAdvPopover}
            sx={advFiltersBtnSx}
          >
            <Badge
              badgeContent={advFilterCount}
              showZero={advFilterCount !== 0}
              color="primary"
              sx={getBadgeIconSx}
            >
              <TuneIcon sx={getDefaultIconSx} />
            </Badge>
          </IconButton>
        </Tooltip>
      </Stack>

      <Box
        data-testid="log-search-active-filters"
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: { xs: 'nowrap', sm: 'wrap' },
          gap: 0.5,
          overflow: 'auto',
        }}
      >
        <LogSearchFilterFields />
      </Box>

      <LogSearchAdvancedPopover />
    </Box>
  );
});

LogSearchTopBar.displayName = 'LogSearchTopBar';
